import styled, { css } from "styled-components";

import {
  LayoutColumn,
  LayoutShell,
  ProfileCopy,
} from "components/layouts/app-layout/styles/app-layout.styles";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { SkeletonBlock } from "components/admin";
import FallbackImage from "components/global/FallbackImage";

export const AppLayoutLayoutShell = styled(LayoutShell).attrs({
  className: "brand-shell",
})``;

export const AppLayoutLayoutColumn = styled(LayoutColumn)``;

export const AppLayoutOrderedList = styled.ol`
  align-items: center;
  display: flex;
  list-style: none;
  margin: 0;
  min-width: 0;
  padding: 0;
  position: relative;
  z-index: 1;
`;

export const AppLayoutMeasureList = styled(AppLayoutOrderedList)`
  left: 0;
  min-width: max-content;
  pointer-events: none;
  position: absolute;
  top: 0;
  visibility: hidden;
  z-index: 0;
`;

const breadcrumbSegmentStyles = css`
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.75rem;
  line-height: 1.25rem;
  max-width: 11rem;
  min-height: 1.75rem;
  overflow: hidden;
  padding: 0.125rem 0.5rem;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AppLayoutListItem = styled.li`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  position: relative;
`;

export const AppLayoutBreadcrumbSeparator = styled.span`
  align-items: center;
  color: var(--brand-muted);
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.625rem;
  justify-content: center;
  width: 1.125rem;
`;

export const AppLayoutFaChevronRight = styled(FaChevronRight).attrs({
  className: "shrink-0",
})``;

export const AppLayoutBreadcrumbLink = styled.a`
  ${breadcrumbSegmentStyles}
  background: transparent;
  border: none;
  color: var(--brand-muted);
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease;

  &:hover {
    background: rgba(240, 191, 86, 0.08);
    color: var(--brand-ink);
  }
`;

export const AppLayoutBreadcrumbTrigger = styled.button`
  ${breadcrumbSegmentStyles}
  background: transparent;
  border: none;
  color: var(--brand-muted);
  cursor: pointer;
  font-family: inherit;
  transition: background 160ms ease, color 160ms ease;

  &:hover,
  &[aria-expanded="true"] {
    background: rgba(240, 191, 86, 0.08);
    color: var(--brand-ink);
  }
`;

export const AppLayoutBreadcrumbCurrent = styled.span`
  ${breadcrumbSegmentStyles}
  color: var(--brand-ink);
  font-weight: 600;
`;

export const AppLayoutBreadcrumbEllipsis = styled.button`
  ${breadcrumbSegmentStyles}
  background: transparent;
  border: none;
  color: var(--brand-muted);
  justify-content: center;
  max-width: 2.25rem;
  min-width: 2.25rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  transition: background 160ms ease, color 160ms ease;

  &:hover,
  &[aria-expanded="true"] {
    background: rgba(240, 191, 86, 0.08);
    color: var(--brand-ink);
  }
`;

export const AppLayoutBreadcrumbMenu = styled.div`
  background: var(--brand-panel-muted);
  border: none;
  border-radius: 1rem;
  box-shadow: var(--brand-dropdown-shadow);
  left: 0;
  max-width: min(16rem, calc(100vw - 1.5rem));
  min-width: 10rem;
  padding: 0.5rem;
  position: absolute;
  top: calc(100% + 0.4rem);
  z-index: 80;
`;

export const AppLayoutBreadcrumbMenuLink = styled.a`
  background: transparent;
  border-radius: 0.75rem;
  color: var(--brand-ink);
  cursor: pointer;
  display: block;
  font-family: inherit;
  font-size: 0.8125rem;
  padding: 0.5rem 0.75rem;
  text-align: left;
  text-decoration: none;
  transition: background 160ms ease;
  width: 100%;

  &:hover,
  &:focus-visible {
    background: rgba(240, 191, 86, 0.08);
    outline: none;
  }
`;

export const AppLayoutFieldShell = styled.div`
  flex-shrink: 0;
  position: relative;
`;

export const AppLayoutSkeletonAvatar = styled(SkeletonBlock)`
  border-radius: 0.75rem;
  flex-shrink: 0;
  height: 2rem;
  width: 2rem;

  @media (min-width: 640px) {
    height: 2.125rem;
    width: 2.125rem;
  }
`;

export const AppLayoutThumbnailImage = styled(FallbackImage)`
  border-radius: 0.75rem;
  flex-shrink: 0;
  height: 2rem;
  object-fit: cover;
  width: 2rem;

  @media (min-width: 640px) {
    height: 2.125rem;
    width: 2.125rem;
  }
`;

export const AppLayoutSection = styled.div`
  align-items: center;
  background: var(--brand-highlight);
  border-radius: 0.75rem;
  color: #1b1306;
  display: flex;
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  height: 2rem;
  justify-content: center;
  width: 2rem;

  @media (min-width: 640px) {
    font-size: 0.8125rem;
    height: 2.125rem;
    width: 2.125rem;
  }
`;

export const AppLayoutProfileCopy = styled(ProfileCopy)``;

export const AppLayoutContentStack = styled.div.attrs({ className: "space-y-2" })``;

export const AppLayoutSkeletonLine = styled(SkeletonBlock)`
  border-radius: 999px;
  height: 0.75rem;
  width: 5.5rem;
`;

export const AppLayoutSkeletonLineSecondary = styled(SkeletonBlock)`
  border-radius: 999px;
  height: 0.5625rem;
  width: 4rem;
`;

export const AppLayoutTextBlock = styled.p`
  color: var(--brand-ink);
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.15;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AppLayoutEyebrowText = styled.p`
  color: var(--brand-muted);
  font-size: 0.625rem;
  letter-spacing: 0.14em;
  line-height: 1.1;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;

  @media (min-width: 1024px) {
    font-size: 0.6875rem;
  }
`;

export const AppLayoutFaChevronDown = styled(FaChevronDown)`
  color: var(--brand-muted);
  flex-shrink: 0;
  font-size: 0.625rem;
  margin-left: 0.0625rem;
`;

export const AppLayoutEyebrowTextSecondary = styled.p.attrs({
  className: "font-anonymous text-xs uppercase tracking-[0.24em] text-[var(--brand-highlight)]",
})``;

export const AppLayoutBodyText = styled.p.attrs({
  className: "mt-1 text-[13px] leading-5 text-[var(--brand-muted)]",
})``;
